import { Link } from 'react-router-dom';
import { StatusBadge } from '@pazportal/ui';
import { useActiveTrip } from '../hooks/useActiveTrip';
import { useDailyReports, latestReport } from '../hooks/useDailyReports';
import { TripPicker } from '../components/TripPicker';
import { LoadingBlock, NoActiveTrip } from '../components/PageState';
import { FactList } from '../components/FactList';
import { formatAddress, formatDate, formatSolutionType, pluralize } from '../lib/formatters';
import { tripDayInfo } from '../lib/tripDates';
import { hrefWithTrip } from '../lib/tripQuery';

export function ProjectPage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const reportsQuery = useDailyReports(trip?.id);

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }

  const day = tripDayInfo(trip.start_date, trip.end_date);
  const address = formatAddress(trip.location?.address);
  const phone = trip.location?.contact_phone;
  const email = trip.location?.contact_email;
  const solutionTypes = trip.solution_types ?? [];
  const reports = reportsQuery.data ?? [];
  const latest = latestReport(reports);

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-text">Project detail</h1>
          <p className="mt-0.5 font-mono text-sm text-text-muted">{trip.project_number}</p>
        </div>
        <StatusBadge status={trip.status ?? 'scheduled'} />
      </div>

      {trips.length > 1 && <TripPicker trips={trips} selectedId={trip.id} onSelect={selectTrip} />}

      <section className="card p-4" aria-labelledby="schedule-heading">
        <h2 id="schedule-heading" className="card-title mb-3">
          Schedule
        </h2>
        <FactList
          facts={[
            { label: 'Start', value: trip.start_date ? formatDate(trip.start_date) : null },
            { label: 'End', value: trip.end_date ? formatDate(trip.end_date) : null },
            { label: 'Today', value: day.label },
            {
              label: 'Reports',
              value: reportsQuery.isLoading
                ? 'Loading…'
                : reportsQuery.isError
                  ? 'Unavailable'
                  : reports.length === 0
                    ? 'None filed yet'
                    : `${pluralize(reports.length, 'report')} · last ${formatDate(latest?.report_date)}`,
            },
          ]}
        />
      </section>

      <section className="card p-4" aria-labelledby="site-heading">
        <h2 id="site-heading" className="card-title mb-3">
          Site
        </h2>
        <FactList
          facts={[
            { label: 'Customer', value: trip.customer?.name ?? null },
            { label: 'Account #', value: trip.customer?.account_number ?? null, mono: true },
            { label: 'Location', value: trip.location?.name ?? null },
            { label: 'Address', value: address },
            { label: 'Site contact', value: trip.location?.contact_name ?? null },
            {
              label: 'Phone',
              value: phone ? (
                <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="text-primary underline">
                  {phone}
                </a>
              ) : null,
            },
            {
              label: 'Email',
              value: email ? (
                <a href={`mailto:${email}`} className="break-all text-primary underline">
                  {email}
                </a>
              ) : null,
            },
          ]}
        />
      </section>

      <section className="card p-4" aria-labelledby="work-heading">
        <h2 id="work-heading" className="card-title mb-3">
          Work order
        </h2>
        <FactList
          facts={[
            { label: 'Lead tech', value: trip.lead_technician?.full_name ?? trip.lead_technician?.email ?? null },
            { label: 'PO #', value: trip.po_number ?? null, mono: true },
            { label: 'Scope', value: trip.scope ? <span className="whitespace-pre-wrap font-normal">{trip.scope}</span> : null },
            {
              label: 'Solutions',
              value:
                solutionTypes.length > 0 ? (
                  <span className="flex flex-wrap gap-1.5">
                    {solutionTypes.map((t) => (
                      <span key={t} className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary">
                        {formatSolutionType(t)}
                      </span>
                    ))}
                  </span>
                ) : null,
            },
          ]}
        />
      </section>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Link to={hrefWithTrip('/daily-report', trip.id)} className="btn-primary">
          Open today&apos;s report
        </Link>
        <Link to={hrefWithTrip('/network', trip.id)} className="btn-secondary">
          Network documentation
        </Link>
      </div>
    </div>
  );
}
